import React, { useEffect, useRef, useState } from "react";
import { Box, Popover, TextField, Avatar, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInstagram,
  faFacebook,
  faYoutube,
  faSoundcloud,
  faMixcloud,
  faTiktok,
  faSpotify,
} from "@fortawesome/free-brands-svg-icons"; // For brand icons
import { faLink } from "@fortawesome/free-solid-svg-icons";

const requiredSocials = ["Instagram", "Facebook", "TikTok"];

const linkRegex =
  /\b((?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/\S*)?)\b/;

const socialOptions = [
  {
    name: "Instagram",
    icon: faInstagram,
    color: "#E4405F",
  },
  {
    name: "Facebook",
    icon: faFacebook,
    color: "#1877F2",
  },
  {
    name: "TikTok",
    icon: faTiktok,
    color: "#000000",
  },
  {
    name: "SoundCloud",
    icon: faSoundcloud,
    color: "#FF7700",
  },
  {
    name: "Spotify",
    icon: faSpotify,
    color: "#1DB954",
  },
  {
    name: "YouTube",
    icon: faYoutube,
    color: "#FF0000",
  },
  {
    name: "Mixcloud",
    icon: faMixcloud,
    color: "#273A61",
  },
  {
    name: "Linktree",
    icon: faLink,
    color: "#43E57D",
  },
];

const Socials = ({ socials, setSocials }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentSocial, setCurrentSocial] = useState("");
  const [error, setError] = useState("");
  const [tempValue, setTempValue] = useState("");
  const textFieldRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(
        window.innerWidth <= 768 ||
          /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          )
      );
    };

    checkMobile();
  }, []);

  useEffect(() => {
    if (anchorEl && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [anchorEl, currentSocial]);

  const socialNames = socialOptions.map((option) => option.name);
  const filledSocials = Object.entries(socials || {})
    .filter(
      ([key, value]) =>
        socialNames.includes(key) &&
        typeof value === "string" &&
        value.trim() !== ""
    )
    .map(([key]) => key);
  const hasRequired = filledSocials.some((social) =>
    requiredSocials.includes(social)
  );
  const hasMinTwo = filledSocials.length >= 2;

  const handleClick = (event, name) => {
    setAnchorEl(event.currentTarget);
    setCurrentSocial(name);
    setError("");
    setTempValue(socials?.[name] || "");
  };

  const handleClose = () => {
    if (error) {
      return;
    }

    if (tempValue.trim() !== "" && linkRegex.test(tempValue)) {
      setSocials((prev) => ({
        ...prev,
        [currentSocial]: tempValue,
      }));
    }

    setAnchorEl(null);
    setCurrentSocial("");
    setError("");
    setTempValue("");
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setTempValue(value);

    const isValid = linkRegex.test(value);
    setError(
      isValid || value === "" ? "" : `Please enter a valid ${currentSocial} URL`
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (!error) {
        setSocials((prev) => ({
          ...prev,
          [currentSocial]: tempValue,
        }));
      }
      handleClose();
    }
  };

  const requiredOptions = socialOptions.filter((opt) =>
    requiredSocials.includes(opt.name)
  );

  const otherOptions = socialOptions.filter(
    (opt) => !requiredSocials.includes(opt.name)
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 2,
      }}
    >
      {/* Instruction rows */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            display: "flex",
            alignItems: "center",
            color: hasRequired ? "#43E57D" : "#E4405F",
            fontWeight: 500,
            mb: 0.2,
          }}
        >
          <span style={{ marginRight: 6 }}>★</span> One of these is required
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: "flex",
            alignItems: "center",
            color: hasMinTwo ? "#43E57D" : "#E4405F",
            fontWeight: 500,
          }}
        >
          <span style={{ marginRight: 6 }}>★</span> Minimum 2 socials are
          required
        </Typography>
      </Box>
      {/* Social icons */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "center",
          mb: 2,
        }}
      >
        {requiredOptions.map(({ name, icon, color }) => (
          <Box
            key={name}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              width: 48,
              height: 48,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#f0f0f0",
                width: 48,
                height: 48,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                transition: "transform 0.2s ease-in-out",
                "&:hover": { transform: "scale(1.1)" },
              }}
              onClick={(event) => handleClick(event, name)}
            >
              <FontAwesomeIcon icon={icon} style={{ color }} size="lg" />
            </Avatar>
            {/* Star in upper right corner, outside the Avatar */}
            <span
              style={{
                color: hasRequired ? "#43E57D" : "#E4405F",
                fontSize: 16,
                position: "absolute",
                top: -8,
                right: -8,
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              ★
            </span>
          </Box>
        ))}
        {otherOptions.map(({ name, icon, color }) => (
          <Avatar
            key={name}
            sx={{
              bgcolor: "#f0f0f0",
              width: 48,
              height: 48,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              transition: "transform 0.2s ease-in-out",
              "&:hover": { transform: "scale(1.1)" },
            }}
            onClick={(event) => handleClick(event, name)}
          >
            <FontAwesomeIcon icon={icon} style={{ color }} size="lg" />
          </Avatar>
        ))}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{
          sx: {
            padding: "12px",
            borderRadius: "12px",
            width: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...(isMobile && {
              position: "fixed !important",
              bottom: "45%",
              left: "50% !important",
              top: "auto !important",
              right: "auto !important",
              transform: "translateX(-50%) !important",
              zIndex: 1300,
              maxHeight: "none",
              maxWidth: "350px",
            }),
          },
        }}
      >
        <TextField
          margin="dense"
          label={`Enter ${currentSocial} URL`}
          variant="outlined"
          value={tempValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          inputRef={textFieldRef}
          autoFocus
          error={Boolean(error)}
          helperText={error || `Enter your ${currentSocial} profile URL`}
          sx={{
            width: "380px",
            "& .MuiInputBase-root": {
              height: "42px",
              color: "#d8c8f4",
              "& fieldset": {
                borderColor: error ? "#E4405F" : "#d8c8f4",
              },
              "&:hover fieldset": {
                borderColor: error ? "#E4405F" : "#d8c8f4",
              },
              "&.Mui-focused fieldset": {
                borderColor: error ? "#E4405F" : "#d8c8f4",
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "12px",
              color: error ? "#E4405F" : "#d8c8f4",
              "&.Mui-focused": {
                color: error ? "#E4405F" : "#d8c8f4",
              },
            },
            "& .MuiOutlinedInput-input": { padding: "6px" },
            "& .MuiFormHelperText-root": {
              color: error ? "#E4405F" : "#d8c8f4",
              fontSize: "12px",
            },
          }}
        />
      </Popover>
    </Box>
  );
};

export default Socials;
